import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Trophy, Heart, MapPin, Quote, ArrowRight, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMPACT_IMAGE = "https://images.unsplash.com/photo-1700914300017-0b90e4fa8773?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHx5b3V0aCUyMHRyYWNrJTIwYW5kJTIwZmllbGQlMjBydW5uaW5nJTIwcmFjZSUyMGRpdmVyc2UlMjBraWRzJTIwYXRobGV0aWNzfGVufDB8fHx8MTc3MjU2MjcyMXww&ixlib=rb-4.1.0&q=85";

const testimonials = [
  {
    quote: "Lane 4 gave my daughter the chance to compete when we couldn't afford the registration fees. She's now one of the top runners in her age group.",
    author: "Maria T.",
    role: "Parent",
  },
  {
    quote: "As a coach, I've seen so many talented kids sit out because of finances. Lane 4 is changing that, one athlete at a time.",
    author: "Coach Williams",
    role: "Youth Track Coach",
  },
  {
    quote: "Thanks to the equipment fund, I got my first pair of real track spikes. I won my first race wearing them!",
    author: "Jordan, Age 14",
    role: "Lane 4 Athlete",
  },
];

const athleteSpotlights = [
  {
    name: "Maya Johnson",
    age: 12,
    sport: "Track & Field",
    achievement: "Regional 400m Champion",
    story: "Maya dreamed of running track but her family couldn't afford the fees. Lane 4 covered her registration and equipment, and she's now competing at the regional level.",
  },
  {
    name: "DeShawn Williams",
    age: 15,
    sport: "Cross Country",
    achievement: "State Qualifier",
    story: "With support from Lane 4's scholarship program, DeShawn has trained with professional coaches and qualified for the state championships.",
  },
  {
    name: "Sofia Martinez",
    age: 10,
    sport: "Soccer",
    achievement: "Travel Team Player",
    story: "When Sofia's family couldn't afford travel team fees, Lane 4 stepped in. She's now playing competitive soccer across the region.",
  },
];

const Impact = () => {
  const [stats, setStats] = useState({
    athletes_supported: 500,
    programs_funded: 25,
    scholarships_awarded: 150,
    communities_reached: 12,
    total_raised: 125000,
  });
  const [athletes, setAthletes] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, athletesRes] = await Promise.all([
          axios.get(`${API}/impact/stats`),
          axios.get(`${API}/athletes`)
        ]);
        setStats(statsRes.data);
        if (athletesRes.data && athletesRes.data.length > 0) {
          setAthletes(athletesRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Use API athletes if available, otherwise use defaults
  const displayAthletes = athletes.length > 0 ? athletes.slice(0, 3) : athleteSpotlights;

  const impactMetrics = [
    { icon: Users, value: stats.athletes_supported, suffix: '+', label: 'Athletes Supported' },
    { icon: Trophy, value: stats.programs_funded, suffix: '', label: 'Programs Funded' },
    { icon: Heart, value: stats.scholarships_awarded, suffix: '+', label: 'Scholarships Awarded' },
    { icon: MapPin, value: stats.communities_reached, suffix: '', label: 'Communities Reached' },
  ];

  return (
    <div className="pt-20" data-testid="impact-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="text-red-500 font-bold uppercase tracking-widest text-sm">Our Impact</span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-4">
                Changing Lives Through Athletics
              </h1>
              <p className="mt-6 text-xl text-slate-300">
                Every donation, volunteer hour, and partnership creates ripples of change in our communities. Here's the impact we're making together.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={IMPACT_IMAGE}
                  alt="Young athletes running"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section className="py-20 md:py-32 bg-white" data-testid="metrics-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">By the Numbers</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4">
              Measuring Our Impact
            </h2>
          </div>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {impactMetrics.map((metric, index) => (
              <motion.div
                key={metric.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="impact-card bg-slate-50 p-8 text-center border border-slate-200"
                data-testid={`metric-${index}`}
              >
                <metric.icon className="w-10 h-10 text-red-600 mx-auto mb-4" />
                <p className="font-heading text-4xl md:text-5xl font-bold text-slate-900">
                  {metric.value.toLocaleString()}{metric.suffix}
                </p>
                <p className="mt-2 text-slate-600 font-medium">{metric.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Total Raised */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 bg-red-600 p-8 text-center text-white"
            data-testid="total-raised"
          >
            <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-80" />
            <p className="font-heading text-5xl md:text-6xl font-bold">
              ${stats.total_raised.toLocaleString()}
            </p>
            <p className="mt-2 text-red-100 text-lg">Total Funds Raised for Young Athletes</p>
          </motion.div>
        </div>
      </section>

      {/* Athlete Spotlights */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="spotlights-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Success Stories</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4">
              Athlete Spotlights
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {displayAthletes.map((athlete, index) => (
              <motion.div
                key={athlete.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-slate-200 overflow-hidden hover:shadow-xl transition-shadow"
                data-testid={`athlete-spotlight-${index}`}
              >
                <div className="h-48 bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center overflow-hidden">
                  {athlete.image_url ? (
                    <img
                      src={athlete.image_url}
                      alt={athlete.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                      <Users className="w-10 h-10 text-white" />
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-xl font-bold text-slate-900">{athlete.name}</h3>
                    <span className="text-sm text-slate-500">Age {athlete.age}</span>
                  </div>
                  <div className="flex items-center space-x-4 mb-4">
                    <span className="text-sm bg-slate-100 px-3 py-1 text-slate-600">{athlete.sport}</span>
                    <span className="text-sm text-red-600 font-medium">{athlete.achievement}</span>
                  </div>
                  <p className="text-slate-600 text-sm">{athlete.story}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-32 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Testimonials</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4">
              What People Are Saying
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="testimonial-card bg-slate-50 p-8 border border-slate-200"
                data-testid={`testimonial-${index}`}
              >
                <Quote className="w-10 h-10 text-red-600 mb-4" />
                <p className="text-slate-700 italic leading-relaxed">"{testimonial.quote}"</p>
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <p className="font-bold text-slate-900">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery Placeholder */}
      <section className="py-20 md:py-32 bg-slate-900" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-500 font-bold uppercase tracking-widest text-sm">Gallery</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4">
              Our Athletes in Action
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div
                key={i}
                className="aspect-square bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"
                data-testid={`gallery-item-${i}`}
              >
                <Trophy className="w-12 h-12 text-slate-600" />
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-slate-400">
            Photo gallery coming soon. Follow us on social media for the latest updates.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Help Us Grow Our Impact
          </h2>
          <p className="mt-4 text-xl text-red-100 max-w-2xl mx-auto">
            With your support, we can reach even more young athletes and communities in need.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donate" data-testid="impact-donate-btn">
              <Button className="bg-white text-red-600 hover:bg-slate-900 hover:text-white rounded-none uppercase tracking-widest font-bold px-10 py-6 text-base">
                Donate Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/get-involved" data-testid="impact-volunteer-btn">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-red-600 rounded-none uppercase tracking-widest font-bold px-10 py-6 text-base">
                Volunteer
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Impact;
